// @flow

import axios from 'axios'
import { redirect } from 'redux-first-router'
import jwtDecode from 'jwt-decode'
import { createFormReducer } from 'react-redux-components'
import { combineReducers } from 'redux'

import { addSuccessNotification } from './notification-manager'

import { historyPush, goToCampaigns } from './router'
import authenticatedRequest from './authenticated-request'
import type { GetState, Dispatch, ReduxAction } from './index'
import type { FormState } from 'react-redux-components'
import type { $AxiosXHR } from 'axios'
import type { NetError } from './authenticated-request'
import type { ReduxState } from './index'
import type { User } from './manage-users'

type UserCredentials = {
  user?: {
    username: string,
    fullname: string,
    role: string,
    email: string,
    changePasswordNeeded?: boolean,
  },
  iat?: number,
  exp?: number,
  iss?: string,
  sub?: string,
  token?: string,
  systemUsers?: Array<User>,
}

// Actions
export const LOGIN_PENDING: 'authentication/LOGIN_PENDING' = 'authentication/LOGIN_PENDING'
export const LOGIN_SUCCESS: 'authentication/LOGIN_SUCCESS' = 'authentication/LOGIN_SUCCESS'
export const LOGIN_ERROR: 'authentication/LOGIN_ERROR' = 'authentication/LOGIN_ERROR'

export const UPDATE_PASSWORD_PENDING: 'authentication/UPDATE_PASSWORD_PENDING' =
  'authentication/UPDATE_PASSWORD_PENDING'
export const UPDATE_PASSWORD_SUCCESS: 'authentication/UPDATE_PASSWORD_SUCCESS' =
  'authentication/UPDATE_PASSWORD_SUCCESS'
export const UPDATE_PASSWORD_ERROR: 'authentication/UPDATE_PASSWORD_ERROR' = 'authentication/UPDATE_PASSWORD_ERROR'

export const TOKEN_EXPIRED: 'authentication/TOKEN_EXPIRED' = 'authentication/TOKEN_EXPIRED'
export const LOGOUT: 'authentication/LOGOUT' = 'authentication/LOGOUT'
export const TOKEN_UPDATE: 'authentication/TOKEN_UPDATE' = 'authentication/TOKEN_UPDATE'

type ACTION_LOGIN_PENDING = {| type: typeof LOGIN_PENDING |}
type ACTION_LOGIN_SUCCESS = {| type: typeof LOGIN_SUCCESS, payload: UserCredentials |}
type ACTION_LOGIN_ERROR = {| type: typeof LOGIN_ERROR, payload: NetError |}

type ACTION_UPDATE_PASSWORD_PENDING = {| type: typeof UPDATE_PASSWORD_PENDING |}
type ACTION_UPDATE_PASSWORD_SUCCESS = {| type: typeof UPDATE_PASSWORD_SUCCESS |}
export type ACTION_UPDATE_PASSWORD_ERROR = {| type: typeof UPDATE_PASSWORD_ERROR, payload: NetError |}

type ACTION_TOKEN_EXPIRED = {| type: typeof TOKEN_EXPIRED |}
type ACTION_LOGOUT = {| type: typeof LOGOUT |}
type ACTION_TOKEN_UPDATE = {| type: typeof TOKEN_UPDATE, payload: string |}

export type AuthenticationAction =
  | ACTION_LOGIN_PENDING
  | ACTION_LOGIN_SUCCESS
  | ACTION_LOGIN_ERROR
  | ACTION_UPDATE_PASSWORD_PENDING
  | ACTION_UPDATE_PASSWORD_SUCCESS
  | ACTION_UPDATE_PASSWORD_ERROR
  | ACTION_TOKEN_EXPIRED
  | ACTION_LOGOUT
  | ACTION_TOKEN_UPDATE

export const statuses = {
  LOGIN_NEW: 'LOGIN_NEW',
  LOGIN_PENDING: 'LOGIN_PENDING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_ERROR: 'LOGIN_ERROR',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  SUCCESS: 'SUCCESS',
  UPDATE_PASSWORD_PENDING: 'UPDATE_PASSWORD_PENDING',
  UPDATE_PASSWORD_ERROR: 'UPDATE_PASSWORD_ERROR',
}

type Status =
  | 'LOGIN_NEW'
  | 'LOGIN_PENDING'
  | 'LOGIN_SUCCESS'
  | 'LOGIN_ERROR'
  | 'TOKEN_EXPIRED'
  | 'SUCCESS'
  | 'UPDATE_PASSWORD_PENDING'
  | 'UPDATE_PASSWORD_ERROR'

type AuthState_ = {|
  status: Status,
  userAuthenticated: boolean,
  credentials: UserCredentials,
  error: {|
    title: string,
    message: string,
  |},
|}
type AuthState = $Shape<AuthState_>

// Initial state
export const initialState: AuthState = {
  status: statuses.LOGIN_NEW,
  userAuthenticated: false,
  credentials: {},
  error: {
    title: '',
    message: '',
  },
}

// Reducer
export const reducer = (state: AuthState = initialState, action: ReduxAction): AuthState => {
  switch (action.type) {
    case LOGIN_PENDING:
      return { ...state, status: statuses.LOGIN_PENDING }
    case LOGIN_SUCCESS:
      return {
        ...state,
        status: statuses.LOGIN_SUCCESS,
        userAuthenticated: true,
        credentials: action.payload,
        error: {
          title: '',
          message: '',
        },
      }
    case LOGIN_ERROR:
      return {
        ...state,
        status: statuses.LOGIN_ERROR,
        userAuthenticated: false,
        credentials: {},
        error: {
          title: 'Login error',
          message: action.payload.response.data.message,
        },
      }
    case TOKEN_EXPIRED:
      return {
        ...state,
        status: statuses.TOKEN_EXPIRED,
        userAuthenticated: false,
        credentials: {},
        error: {
          title: 'Sessione scaduta',
          message: 'Rieffettua il login',
        },
      }
    case TOKEN_UPDATE:
      return { ...state, userAuthenticated: true, credentials: { ...jwtDecode(action.payload), token: action.payload } }
    case LOGOUT:
      let newStatus = state.status === statuses.TOKEN_EXPIRED ? state.status : statuses.LOGIN_NEW
      return { ...state, status: newStatus, userAuthenticated: false, credentials: {} }
    case UPDATE_PASSWORD_PENDING:
      return { ...state, status: statuses.UPDATE_PASSWORD_PENDING }
    case UPDATE_PASSWORD_SUCCESS:
      return { ...state, status: statuses.SUCCESS }
    case UPDATE_PASSWORD_ERROR:
      return {
        ...state,
        status: statuses.UPDATE_PASSWORD_ERROR,
        error: {
          title: 'Error',
          message: action.payload.response.data.message,
        },
      }

    default:
      return state
  }
}
export type AuthenticationState = {
  data: AuthState,
  form: FormState,
  changePassForm: FormState,
}
export type LoginForm = {
  username: string,
  password: string,
}
export type ChangePasswordForm = {
  oldPassword: string,
  newPassword: string,
  newPasswordConfirm: string,
}
export const authenticationReducer = combineReducers({
  data: reducer,
  form: createFormReducer('auth.form', { username: '', password: '' }),
  changePassForm: createFormReducer('auth.changePassForm', {
    oldPassword: '',
    newPassword: '',
    newPasswordConfirm: '',
  }),
})

// Action creators
export const login = (credentials: LoginForm) => async (dispatch: Dispatch, getState: GetState) => {
  const loginUrl = '/api/users/login'
  let token = ''
  dispatch({ type: LOGIN_PENDING })
  try {
    let response: $AxiosXHR<*> = await axios.post(loginUrl, credentials)
    token = response.data.data
    localStorage.setItem('auth-token', token)
    let decodedToken = jwtDecode(token)
    dispatch({ type: LOGIN_SUCCESS, payload: { ...decodedToken, token } })
    dispatch(redirect(goToCampaigns()))
  } catch (err) {
    localStorage.removeItem('auth-token')
    dispatch({
      type: LOGIN_ERROR,
      payload: err,
    })
  }
}

export const logout = () => (dispatch: Dispatch) => {
  localStorage.removeItem('auth-token')
  dispatch({ type: 'CLEAR_STORE' })
  historyPush('/login')
}

export const updatePassword = (username: string, data: ChangePasswordForm) => async (
  dispatch: Dispatch,
  getState: GetState
) => {
  const updateUrl = '/api/users/' + encodeURIComponent(username) + '/change_password'
  dispatch({ type: UPDATE_PASSWORD_PENDING })
  try {
    let response: $AxiosXHR<*> = await authenticatedRequest.put(updateUrl, data)
    dispatch({ type: UPDATE_PASSWORD_SUCCESS })
    dispatch(redirect(goToCampaigns()))
    dispatch(addSuccessNotification(response.data.message))
  } catch (err) {
    dispatch({
      type: UPDATE_PASSWORD_ERROR,
      payload: err,
    })
  }
}

// Middleware
// $FlowFixMe
export const middleware = store => dispatch => action => {
  if (/.*_SUCCESS$/.test(action.type) && action.type !== LOGIN_SUCCESS) {
    // Refresh token on all successuful HTTP requests
    let token = localStorage.getItem('auth-token')
    if (token) {
      dispatch({ type: TOKEN_UPDATE, payload: token })
    }
  } else if (/.*_ERROR$/.test(action.type) && action.type !== LOGIN_ERROR && action.type !== UPDATE_PASSWORD_ERROR) {
    // Force logout on Unauthorized HTTP requests
    if (action.payload.response.status === 401) {
      localStorage.removeItem('auth-token')
      dispatch(action)
      historyPush('/login')
      dispatch({ type: 'CLEAR_STORE' })
      return dispatch({ type: TOKEN_EXPIRED })
    }
  } else if (/^router.*/.test(action.type) && !/.*LOGOUT_ROUTE$/.test(action.type)) {
    // Check auth status on client-side routing changes
    let sessionToken = localStorage.getItem('auth-token')
    let storeToken =
      'token' in store.getState().auth.data.credentials ? store.getState().auth.data.credentials.token : undefined
    if (sessionToken && jwtDecode(sessionToken).exp > Math.round(Date.now() / 1000)) {
      // Reload app with a valid token on localStorage
      if (sessionToken !== storeToken) dispatch({ type: TOKEN_UPDATE, payload: sessionToken })

      if (/.*LOGIN_ROUTE$/.test(action.type)) {
        return dispatch(redirect(goToCampaigns()))
      }
    } else {
      if (!sessionToken) {
        dispatch({ type: LOGOUT })
      } else {
        localStorage.removeItem('auth-token')
        dispatch({ type: 'CLEAR_STORE' })
        dispatch({ type: TOKEN_EXPIRED })
      }
      return historyPush('/login')
    }
  }
  return dispatch(action)
}

// Selectors
export const isAuthenticated = (state: ReduxState): boolean => {
  return state.auth.data.userAuthenticated
}
export const isAdmin = (state: ReduxState): boolean => {
  return state.auth.data.credentials.user ? state.auth.data.credentials.user.role === 'admin' : false
}
export const isEditor = (state: ReduxState): boolean => {
  return state.auth.data.credentials.user ? state.auth.data.credentials.user.role === 'editor' : false
}
export const isViewer = (state: ReduxState): boolean => {
  return state.auth.data.credentials.user ? state.auth.data.credentials.user.role === 'viewer' : false
}
export const forceChangePassword = (state: ReduxState): boolean => {
  return state.auth.data.credentials.user ? state.auth.data.credentials.user.changePasswordNeeded === true : false
}
export const getFullname = (state: ReduxState): string => {
  return state.auth.data.credentials.user ? state.auth.data.credentials.user.fullname : ''
}
export const getUsername = (state: ReduxState): string => {
  return state.auth.data.credentials.user ? state.auth.data.credentials.user.username : ''
}
export const getSystemUsers = (state: ReduxState): Array<User> => {
  return state.auth.data.credentials.systemUsers ? state.auth.data.credentials.systemUsers : []
}



// WEBPACK FOOTER //
// ./src/store/authentication.js